import React, { useState } from 'react'
import { ImageOff } from 'lucide-react'
import { BrandAppearance, RefZone } from '../../../services/broadcastAdminService'
import { secs, pct, areaSec } from '../broadcastReviewConstants'
import { valueOf, formatMoney } from '../broadcastValuation'

export type FrameSort = 'time' | 'area' | 'size'

/**
 * The order the frame grid shows, and so the order its viewer steps through.
 *
 * Area-seconds rather than value for "most valuable": value is area-seconds
 * times one rate for the whole broadcast, so the two orders are identical,
 * and area-seconds still sorts when the broadcast is not priced.
 */
export const sortAppearances = (rows: BrandAppearance[], sort: FrameSort): BrandAppearance[] => {
  const at = (r: BrandAppearance) => r.peak_frame_ms ?? r.start_time_ms
  const sorted = [...rows]
  if (sort === 'time') sorted.sort((a, b) => at(a) - at(b))
  if (sort === 'area') sorted.sort((a, b) => Number(b.area_seconds ?? 0) - Number(a.area_seconds ?? 0))
  if (sort === 'size') sorted.sort((a, b) => Number(b.max_area_share ?? 0) - Number(a.max_area_share ?? 0))
  return sorted
}

const SORTS: { id: FrameSort; label: string }[] = [
  { id: 'time', label: 'Time' },
  { id: 'area', label: 'Most valuable' },
  { id: 'size', label: 'Largest logo' }
]

interface Props {
  /** Already in display order; see sortAppearances. */
  rows: BrandAppearance[]
  zones: RefZone[]
  sort: FrameSort
  onSort: (s: FrameSort) => void
  frameUrl: (appearanceId: string) => string
  rate: number | null
  currency: string
  brandName: string
  onOpen: (row: BrandAppearance) => void
}

/**
 * Every appearance of one brand as a frame, three across.
 *
 * A contact sheet for scanning, not for reviewing: opening a card hands it
 * to the same viewer the player view uses, in a modal, so playing a clip
 * never moves the grid and closing it returns to the same scroll position.
 *
 * LAZY BY NECESSITY. A frame not yet cached is cut from the video by scout
 * on request, one ffmpeg seek each, so a grid that fetched every frame on
 * open would fire one seek per appearance at once. Native lazy loading only
 * asks for the cards near the viewport; the frames it warms are cached for
 * every later view.
 */
const BrandFrameGrid: React.FC<Props> = ({
  rows, zones, sort, onSort, frameUrl, rate, currency, brandName, onOpen
}) => {
  const [failed, setFailed] = useState<Set<string>>(new Set())
  const zoneLabel = (code: string) => zones.find((z) => z.code === code)?.label ?? code

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-xs text-gray-500">
          {rows.length} {rows.length === 1 ? 'appearance' : 'appearances'} · select a frame to view or replay it
        </p>
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-gray-400">Sort</span>
          <select value={sort} onChange={(e) => onSort(e.target.value as FrameSort)}
                  className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-800">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => {
          const at = r.peak_frame_ms ?? r.start_time_ms
          const value = valueOf(Number(r.area_seconds ?? 0), rate).value
          const gone = !!r.excluded_reason
          return (
            <button key={r.id} type="button" onClick={() => onOpen(r)}
                    className={`group text-left bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden
                                hover:border-blue-400 hover:shadow-md transition ${gone ? 'opacity-50' : ''}`}>
              <div className="relative aspect-video bg-gray-900">
                {failed.has(r.id) ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 gap-1">
                    <ImageOff className="h-5 w-5" />
                    <span className="text-[11px]">Frame unavailable</span>
                  </div>
                ) : (
                  <img src={frameUrl(r.id)} alt={`${brandName} at ${secs(at)}`}
                       loading="lazy" decoding="async"
                       className="w-full h-full object-contain"
                       onError={() => setFailed((prev) => new Set(prev).add(r.id))} />
                )}
                <span className="absolute top-2 left-2 rounded bg-black/70 px-1.5 py-0.5 text-[11px] font-medium text-white tabular-nums">
                  {secs(at)}
                </span>
                {gone && (
                  <span className="absolute top-2 right-2 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-gray-600">
                    Excluded
                  </span>
                )}
              </div>
              <div className="px-3 py-2.5">
                <p className="text-xs font-medium text-gray-800 truncate">{zoneLabel(r.zone_code)}</p>
                <div className="grid grid-cols-3 gap-2 mt-1.5 text-[11px]">
                  {[
                    ['Value', formatMoney(value, currency)],
                    ['Area-sec', areaSec(r.area_seconds)],
                    ['Max size', pct(r.max_area_share)]
                  ].map(([k, v]) => (
                    <div key={k} className="min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{k}</p>
                      <p className="font-semibold text-gray-900 tabular-nums truncate">{v}</p>
                    </div>
                  ))}
                </div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default BrandFrameGrid
